<template>
  <paginated-data-table
    ref="table"
    :headers="headers"
    :search="search"
    :query="query"
    :custom-items-per-page="10"
    :queryName="includeMariaCampaign ? 'genericCampaigns' : 'campaigns'"
    :queryMainPropertyName="includeMariaCampaign ? 'campaigns' : ''"
    :queryVariables="queryVariables"
    :default-table-sort-by="defaultTableSortBy"
    :default-table-sort-desc="defaultTableSortDesc"
    :graphql-uri="$microservicesUrls['campaigns']"
  >
    <template v-slot:item="{ item }">
      <tr class="cursor-pointer" @click="toggleCampaign(item)">
        <td>
          <v-simple-checkbox :value="selectedCampaignsIds.includes(item._id)" color="primary" :ripple="false" @input="toggleCampaign(item)" />
        </td>

        <td class="nowrap-row">
          <v-tooltip v-model="showTooltipTitle[item._id]" max-width="250" top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <router-link
                  v-if="item.status === 'FINISHED'"
                  class="link"
                  :to="{ path: getCampaignRouteLink(item._id, item.mode, isMariaCampaign(item)) }"
                  target="_blank"
                >
                  {{ getFormatedText(item.title, 24) }}
                </router-link>
                <span v-else>{{ getFormatedText(item.title, 24) }}</span>
              </span>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
        </td>

        <td class="nowrap-row">
          <v-tooltip v-model="showTooltipDescription[item._id]" max-width="250" top>
            <template v-slot:activator="{ on }">
              <span v-on="on">{{ getFormatedText(item.description, 48) }}</span>
            </template>
            <span>{{ item.description }}</span>
          </v-tooltip>
        </td>

        <td v-if="item.mode" class="nowrap-row">
          <span>{{ getFormattedMode(item.mode) }}</span>
        </td>

        <td v-else-if="item.channels" class="nowrap-row">
          <span v-if="getCampaingsLength(item.channels) === 1">{{ getFormatedMariaCampaignChannels(item.channels) }}</span>

          <v-tooltip v-else top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ getCampaingsLength(item.channels) }} canais</span>
            </template>
            <span>{{ getFormatedMariaCampaignChannels(item.channels) }}</span>
          </v-tooltip>
        </td>

        <td v-if="showMessage" class="nowrap-row">
          <v-tooltip max-width="250" top>
            <template v-slot:activator="{ on }">
              <div class="message-ellipsis">
                <span v-on="on">{{ item.message !== null ? getFormatedText(item.message.text, 60) : '' }}</span>
              </div>
            </template>
            <span>{{ item.message !== null ? item.message.text : '' }}</span>
          </v-tooltip>
        </td>

        <td>
          {{
            item.initial_date && item.final_date
              ? `${DateTime.fromISO(item.initial_date.toString()).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(item.final_date.toString()).toFormat(
                  'dd/MM/yyyy'
                )}`
              : '-'
          }}
        </td>
      </tr>
    </template>
  </paginated-data-table>
</template>

<script>
import PaginatedDataTable from '@/components/PaginatedDataTable'
import { QUERY_GET_GENERIC_CAMPAIGNS, QUERY_GET_CAMPAIGNS } from '@/modules/campanhas/views/components/graphql'
import { truncate } from 'lodash'
import { DateTime } from 'luxon'
import { statusCampaignArrayValidator } from '@/helpers/propValidators.js'
import { deepDelete } from '@/helpers/deepDelete'

export default {
  name: 'SelectCampaignsTable',
  components: {
    PaginatedDataTable,
  },
  props: {
    value: {
      type: Array,
      required: true,
    },
    showMessage: {
      type: Boolean,
      default: false,
    },
    singleCheckbox: {
      type: Boolean,
      default: false,
    },
    includeMariaCampaign: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: '',
    },
    mode: {
      type: Array,
      default: () => ['SMS', 'EMAIL', 'WHATSAPP', 'PUSH'],
    },
    status: {
      type: Array,
      default: () => [],
      validator: statusCampaignArrayValidator,
    },
    defaultTableSortBy: {
      type: String,
      default: '',
    },
    defaultTableSortDesc: {
      type: Boolean,
      default: false,
    },
    useCampaignFrequency: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showTooltipTitle: {},
    showTooltipDescription: {},
    selectedCampaignsIds: [],
    DateTime: DateTime,
  }),
  computed: {
    query() {
      return this.includeMariaCampaign ? QUERY_GET_GENERIC_CAMPAIGNS : QUERY_GET_CAMPAIGNS
    },
    queryVariables() {
      const mode = this.includeMariaCampaign ? 'channels' : 'mode'
      return {
        search: this.search,
        status: this.status,
        [mode]: this.mode,
        frequency: this.useCampaignFrequency ? 'ONETIME' : null,
      }
    },
    selectedCampaigns: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    headers() {
      if (!this.showMessage) {
        return [
          {
            value: '',
            sortable: false,
            width: '5%',
          },
          {
            text: 'Campanha',
            value: 'title',
            width: '25%',
          },
          {
            text: 'Descrição',
            value: 'description',
            width: '25%',
          },
          {
            text: 'Canal',
            value: 'mode',
            width: '15%',
          },
          {
            text: 'Data de validade',
            value: 'final_date',
            width: '30%',
          },
        ]
      } else {
        return [
          {
            value: '',
            sortable: false,
            width: '5%',
          },
          {
            text: 'Campanha',
            value: 'title',
            width: '20%',
          },
          {
            text: 'Descrição',
            value: 'description',
            width: '20%',
          },
          {
            text: 'Canal',
            value: 'mode',
            width: '10%',
          },
          {
            text: 'Mensagem',
            value: 'message',
            width: '20%',
          },
          {
            text: 'Data de validade',
            value: 'final_date',
            width: '25%',
          },
        ]
      }
    },
  },
  watch: {
    selectedCampaigns: {
      deep: true,

      handler(value) {
        this.selectedCampaignsIds = value.map(({ _id }) => _id)
      },
    },
  },
  mounted() {
    if (this.selectedCampaigns) this.selectedCampaignsIds = this.selectedCampaigns.map(({ _id }) => _id)
  },
  methods: {
    isMariaCampaign(campaign) {
      return Boolean(campaign.channels)
    },
    getFormattedMode(mode) {
      const formats = {
        SMS: 'SMS',
        EMAIL: 'E-mail',
        WHATSAPP: 'Whatsapp',
        PUSH: 'Push App',
      }

      return formats[mode]
    },
    getCampaignRouteLink(_id, mode, isMariaCampaign) {
      if (isMariaCampaign) return `/maria/campanha-da-maria/resultados/${_id}`

      const modesDescription = {
        WHATSAPP: 'whatsapp',
        SMS: 'sms',
        PUSH: 'aplicativo',
        EMAIL: 'email',
      }

      return `/campanhas/${modesDescription?.[mode]}/resultados/${_id}`
    },
    toggleCampaign(toggledCampaign) {
      // Get current selected campaigs without the toggled campaign
      // if it is the same it means that the toggled campaign is being added
      // if not, remove it.
      if (this.singleCheckbox) {
        this.selectedCampaigns = [toggledCampaign]
        return
      }
      const selectedCampaignsDelta = this.selectedCampaigns.filter((campaign) => campaign._id !== toggledCampaign._id)

      if (selectedCampaignsDelta.length === this.selectedCampaigns.length) {
        selectedCampaignsDelta.push(toggledCampaign)
      }
      this.selectedCampaigns = selectedCampaignsDelta
    },
    getFormatedText(text, length) {
      return truncate(text, {
        length,
        separator: ' ',
      })
    },
    getCampaingsLength(channels) {
      return this.getMariaCampaignChannels(channels).length
    },
    getMariaCampaignChannels(channels) {
      const campaignChannels = []
      deepDelete(channels, '__typename')
      Object.entries(channels).forEach((channel) => (channel[1] ? campaignChannels.push(this.getFormattedMode(channel[0].toUpperCase())) : ''))
      return campaignChannels
    },
    getFormatedMariaCampaignChannels(channels) {
      return this.getMariaCampaignChannels(channels)
        .map((channel) => channel)
        .join(', ')
    },
    link(param, type, mode = '') {
      let path = ''

      if (type === 'OMNI_CHANNEL') {
        path = `/#/maria/campanha-da-maria/resultados/${param}`
      } else {
        const routeName = mode === 'PUSH' ? 'aplicativo' : mode
        path = mode === 'WHATSAPP' ? `/campanhas/${routeName.toLowerCase()}/resultados/${param}` : `/campanhas/${routeName.toLowerCase()}/${param}`
      }
      return path
    },
  },
}
</script>

<style lang="scss">
.link-color {
  color: $marca-0-l40 !important;
}
</style>
